<template>
  <div>
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>
    <div class="page-wrapper">
      <div class="content container-fluid">
        <div class="page-header">
          <div class="row align-items-center" style="width: -webkit-fill-available;">
            <div class="col-md-8">
              <h3 class="page-title mb-1">Journal d'activité</h3>
              <p class="text-muted mb-3">
                Historique des opérations réalisées par les utilisateurs sur l'espace support.
              </p>
            </div>
            <div class="col-md-4 text-md-end mt-3 mt-md-0">
              <button class="btn btn-outline-secondary" :disabled="isLoading" @click="loadLogs">
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                Rafraîchir
              </button>
            </div>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-body">
            <div class="row g-2">
              <div class="col-md-4">
                <label class="form-label text-muted mb-1">Utilisateur</label>
                <input v-model="filters.user_email" type="text" class="form-control" placeholder="Email utilisateur" />
              </div>
              <div class="col-md-3">
                <label class="form-label text-muted mb-1">Action</label>
                <select v-model="filters.action" class="form-select">
                  <option value="">Toutes les actions</option>
                  <option v-for="action in actionOptions" :key="action" :value="action">
                    {{ action }}
                  </option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label text-muted mb-1">Méthode</label>
                <select v-model="filters.method" class="form-select">
                  <option value="">Toutes</option>
                  <option v-for="method in methodOptions" :key="method" :value="method">
                    {{ method }}
                  </option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label text-muted mb-1">Du</label>
                <input v-model="filters.date_from" type="date" class="form-control" />
              </div>
              <div class="col-md-2">
                <label class="form-label text-muted mb-1">Au</label>
                <input v-model="filters.date_to" type="date" class="form-control" />
              </div>
              <div class="col-md-2">
                <label class="form-label text-muted mb-1">Société</label>
                <input
                  v-model="filters.societe_name"
                  type="text"
                  class="form-control"
                  placeholder="Nom ou RC"
                />
              </div>
              <div class="col-md-1 d-flex align-items-end">
                <button class="btn btn-primary w-100" :disabled="isLoading" @click="applyFilters">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
                  <span v-else>Filtrer</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div v-if="isLoading" class="text-center py-5">
              <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
            </div>

            <template v-else>
              <div v-if="logs.length === 0" class="text-center text-muted py-5">
                Aucun enregistrement trouvé.
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead class="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Utilisateur</th>
                      <th>Action</th>
                      <th>Portée</th>
                      <th>Société</th>
                      <th>Détails</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="log in logs" :key="log.id">
                      <td>{{ formatDateTime(log.created_at) }}</td>
                      <td>
                        <div class="fw-semibold">{{ log.user_name || "—" }}</div>
                        <div class="text-muted small">{{ log.user_email || "—" }}</div>
                      </td>
                      <td>{{ log.action }}</td>
                      <td>{{ log.scope || "—" }}</td>
                      <td>
                        <div v-if="resolveSocieteName(log)" class="text-muted small">
                          {{ resolveSocieteName(log) }}
                        </div>
                        <span v-else>—</span>
                      </td>
                      <td class="align-middle">
                        <div class="properties-wrapper">
                          <pre class="properties-pre">{{ formatProperties(log.properties) }}</pre>
                          <button
                            class="btn btn-sm btn-outline-secondary ms-2"
                            type="button"
                            @click="copyProperties(log.properties)"
                          >
                            <i class="fas fa-copy me-1"></i>Copier
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <nav v-if="pagination.total > 0" aria-label="Pagination" class="mt-3">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-2">
                    <label class="small text-muted mb-0">Afficher</label>
                    <select class="form-select form-select-sm" v-model.number="pagination.per_page" @change="changePerPage" style="width: auto;">
                      <option :value="10">10</option>
                      <option :value="25">25</option>
                      <option :value="50">50</option>
                      <option :value="100">100</option>
                    </select>
                    <span class="small text-muted"> / {{ pagination.total }} éléments</span>
                  </div>

                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: pagination.current_page <= 1 }">
                      <button class="page-link" @click="goToPage(pagination.current_page - 1)" :disabled="pagination.current_page <= 1">Précédent</button>
                    </li>

                    <li
                      v-for="p in visiblePages"
                      :key="p"
                      class="page-item"
                      :class="{ active: p === pagination.current_page }"
                    >
                      <button class="page-link" @click="goToPage(p)">{{ p }}</button>
                    </li>

                    <li class="page-item" :class="{ disabled: pagination.current_page >= totalPages }">
                      <button class="page-link" @click="goToPage(pagination.current_page + 1)" :disabled="pagination.current_page >= totalPages">Suivant</button>
                    </li>
                  </ul>
                </div>
              </nav>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, computed } from "vue";
import { toast } from "vue3-toastify";
import { fetchActivityLogs } from "@/services/devActivityService";
import { fetchAllSocietes } from "@/services/devSupportService";

const isLoading = ref(false);
const logs = ref([]);
const actionOptions = ref([]);
const methodOptions = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const societeLookup = ref({});
const pagination = reactive({
  current_page: 1,
  per_page: 25,
  total: 0,
  next_page_url: null,
  prev_page_url: null,
});

const filters = reactive({
  user_email: "",
  action: "",
  method: "",
  date_from: "",
  date_to: "",
  societe_name: "",
});

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR");
};

const formatProperties = (properties) => {
  if (!properties) return "{}";
  try {
    return JSON.stringify(properties, null, 2);
  } catch (error) {
    return String(properties);
  }
};

const copyProperties = async (properties) => {
  const text = formatProperties(properties);
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Détails copiés dans le presse-papiers.");
  } catch (error) {
    toast.error("Impossible de copier les détails.");
  }
};

const resolveSocieteName = (log) => {
  const properties = log?.properties ?? {};
  const directName = properties?.societe_nom || properties?.societe_name;
  if (directName) {
    const rc = properties?.societe_rc || properties?.rc;
    return rc ? `${directName} (RC ${rc})` : directName;
  }

  const payload = properties?.payload ?? {};
  const nestedSociete = payload?.societe || payload?.company;

  let name = payload?.societe_nom || nestedSociete?.societe_nom || nestedSociete?.name;
  const rc = payload?.societe_rc || payload?.rc || nestedSociete?.rc;

  let cached = null;
  if (!name && payload?.societe_id) {
    cached = societeLookup.value[payload.societe_id] || null;
    if (cached) {
      name = cached.nom;
      if (!rc && cached.rc) {
        return `${cached.nom} (RC ${cached.rc})`;
      }
    }
  }

  if (name && rc) {
    return `${name} (RC ${rc})`;
  }

  if (name) {
    return name;
  }

  if (!cached && payload?.societe_id) {
    cached = societeLookup.value[payload.societe_id] || null;
  }

  if (cached) {
    return cached.rc ? `${cached.nom} (RC ${cached.rc})` : cached.nom;
  }

  if (rc) {
    return `RC ${rc}`;
  }

  return "";
};

const loadLogs = async (page = 1) => {
  isLoading.value = true;
  try {
    const response = await fetchActivityLogs({
      page,
      per_page: pagination.per_page,
      ...filters,
    });

    const payload = response?.data ?? {};
    logs.value = payload.data ?? [];
    pagination.current_page = payload.current_page ?? 1;
    pagination.per_page = payload.per_page ?? 25;
    pagination.total = payload.total ?? 0;
    pagination.next_page_url = payload.next_page_url;
    pagination.prev_page_url = payload.prev_page_url;

    const uniqueActions = new Set(actionOptions.value);
    for (const entry of logs.value) {
      if (entry?.action) {
        uniqueActions.add(entry.action);
      }
    }
    actionOptions.value = Array.from(uniqueActions)
      .filter(Boolean)
      .sort();
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Impossible de charger le journal.";
    toast.error(message);
  } finally {
    isLoading.value = false;
  }
};

const goToPage = (page) => {
  if (!page) return;
  const p = Number(page);
  if (p < 1 || p > totalPages.value) return;
  loadLogs(p);
};

const totalPages = computed(() => {
  return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.per_page || 25)));
});

// return a windowed set of pages (max 7 pages shown)
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = Number(pagination.current_page || 1);
  const maxVisible = 7;
  if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);

  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  let end = start + maxVisible - 1;
  if (end > total) {
    end = total;
    start = end - maxVisible + 1;
  }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

const changePerPage = () => {
  // pagination.per_page is v-model bound; reset to page 1 and reload
  loadLogs(1);
};

const applyFilters = () => {
  loadLogs(1);
};

onMounted(() => {
  loadLogs();
  fetchAllSocietes()
    .then((response) => {
      const societes = response?.data || [];
      const map = {};
      societes.forEach((soc) => {
        map[soc.id] = {
          nom: soc.societe_nom,
          rc: soc.rc,
        };
      });
      societeLookup.value = map;
    })
    .catch(() => {
      societeLookup.value = {};
    });
});
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-word;
}

.properties-wrapper {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.5rem;
  max-width: 560px;
}

.properties-pre {
  flex: 1;
  margin: 0;
  font-family: 'JetBrains Mono', 'Fira Code', Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.72rem;
  background: #0f172a;
  color: #a5f3fc;
  border-radius: 0.5rem;
  padding: 0.6rem 0.75rem;
  line-height: 1.35;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
  max-height: 220px;
  overflow: auto;
}

.properties-wrapper .btn {
  white-space: nowrap;
  align-self: flex-start;
}
</style>
